/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react'
import { State, useHookstate } from '@hookstate/core'
import { Validation as validation } from '@hookstate/validation'
import { Box, Grid, IconButton, TextField, Tooltip } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useEffect, useRef, useState } from 'react'

dayjs.extend(utc)
import { DispatchSearchBox } from '~/components/DispatchSearchBox'
import FilterAutocompleteCamera from '~/components/FilterAutocompleteCamera'
import InputDateTime from '~/components/InputDate'
import { InputSearchWithClear } from '~/components/InputSearchWithClear'
import notify from '~/utils/notify'
import { useLang } from '../../../../../../hooks/useLang'
import useSurveillanceReportingState from '../../../../stores/SurveillanceReportingState'
import TypeSubtypeModal from '../NewSurveillanceReporting/components/TypeSubtypeModal'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import { List, ListItem, Paper, Typography } from '@mui/material'
import { EditOff } from '@mui/icons-material'

interface NewSurveillanceReportingProps {
  readonly invalidRequest: State<boolean>
}
// eslint-disable-next-line max-lines-per-function
export default function NewSurveillanceReporting({
  invalidRequest,
}: NewSurveillanceReportingProps) {
  const { translate } = useLang()
  const {
    surveillanceReportingStates,
    sendNewSurveillanceReporting,
    editSurveillanceReporting,
    verifyInputErros,
    associateOccurrence,
  } = useSurveillanceReportingState()
  const {
    surveillanceReporting,
    loading,
    save,
    searchDispatches,
    searchOccurrence,
    showCheckbox,
    createOccurrence,
    verifyType,
    verifySubtype,
    newNarrations,
    isEditMode,
    isAssociateMode,
    selectedReportsForAssociation,
    reports,
  } = surveillanceReportingStates()
  const { notes, cameraId, reportedAt, id, occurrenceId, typeId, subtypeId } =
    surveillanceReporting

  const cameraIdVal = cameraId.value
  const searchOccurrenceVal = searchOccurrence.value
  const reportedAtVal = reportedAt.value
  const createOccurrenceVal = createOccurrence.value

  const [dateOfReport, setDateOfReport] = useState<Dayjs | null>(null)
  const isSearchDialog = useHookstate(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const occurrenceInputRef = useRef<HTMLInputElement>(null)
  const latitudeCamera = useHookstate<number | null>(0)
  const longitudeCamera = useHookstate<number | null>(0)
  const enabledField = typeId?.value !== null
  const openTypeModal = useHookstate(false)

  const checkedReports = reports.filter((report) => report.checked.value)
  const hasCheckedReports = checkedReports.length > 0
  const checkedReportsCount = checkedReports.length

  const handleClearInputs = () => {
    setDateOfReport(null)
    notes.set('')
    searchOccurrence.set('')
    searchDispatches.set('')
    reportedAt.set(null)
    save.set(false)
    occurrenceId.set(null)
    latitudeCamera.set(null)
    longitudeCamera.set(null)
    cameraId.set(null)
    id.set(undefined)
    invalidRequest.set(false)
    subtypeId.set(null)
    typeId.set(null)
    verifyType.set(false)
    verifySubtype.set(true)
    createOccurrence.set(false)
    isEditMode.set(false)
    isAssociateMode.set(false)
    selectedReportsForAssociation.set([])
  }

  const handleCancelEdit = () => {
    handleClearInputs()

    reports.forEach((report) => {
      if (report.checked.get()) {
        report.checked.set(false)
      }
    })

    showCheckbox.set(false)
  }

  const handleCancelAssociation = () => {
    reports.forEach((report) => report.checked.set(false))
    selectedReportsForAssociation.set([])
    createOccurrence.set(false)
    showCheckbox.set(false)
    handleClearInputs()
  }

  const handleSend = () => {
    loading.set(true)

    if (showCheckbox.get()) {
      const reportIds = reports
        .filter((report) => report.checked.get())
        .map((report) => report.id.get())
      const checkedReports = reports.filter((report) => report.checked.get())
      const sortedReports = [...checkedReports].sort(
        (reportA, reportB) =>
          new Date(reportA.createdAt.get() || '').getTime() -
          new Date(reportB.createdAt.get() || '').getTime()
      )
      const oldestReport = sortedReports[0]

      associateOccurrence({
        occurrenceId: !createOccurrence.get()
          ? occurrenceId.get() ?? null
          : null,
        surveillanceReportIds: reportIds,
        toCreateOccurrence: createOccurrence.get(),
        typeId: typeId.get(),
        subtypeId: subtypeId.get(),
        latitude: latitudeCamera.get(),
        longitude: longitudeCamera.get(),
        cameraId: oldestReport?.cameraId.get() || null,
      })
        .then((response) => {
          if (response?.status === 200) {
            notify({
              message: translate('Reports associated with success'),
              type: 'success',
            })
            handleClearInputs()
            loading.set(false)
            newNarrations.set(!newNarrations.get())
            showCheckbox.set(false)
          }
        })
        .catch(() => {
          loading.set(false)
        })
      return
    }

    if (save.get()) {
      editSurveillanceReporting({
        id: id.get(),
        cameraId: cameraId.get(),
        occurrenceId: occurrenceId.get() || null,
        reportedAt: dayjs(reportedAt.get()).utc().toJSON(),
        notes: notes.get(),
      })
        .then((response) => {
          if (response?.status === 200) {
            notify({
              message: translate('Narration saved successfully'),
              type: 'success',
            })
            handleClearInputs()
            loading.set(false)
            newNarrations.set(!newNarrations.get())
          }
        })
        .catch(() => {
          loading.set(false)
        })
    } else {
      if (verifyInputErros()) {
        sendNewSurveillanceReporting({
          occurrenceId: !createOccurrence.get() ? occurrenceId.get() : null,
          cameraId: cameraId.get(),
          notes: notes.get(),
          reportedAt: dayjs(reportedAt.get()).utc().toJSON(),
          toCreateOccurrence: createOccurrence.get(),
          typeId: typeId.get(),
          subtypeId: subtypeId.get(),
          latitude: latitudeCamera.get(),
          longitude: longitudeCamera.get(),
        })
          .then((response) => {
            if (response.status === 200) {
              notify({
                message: translate('Narration saved successfully'),
                type: 'success',
              })
              handleClearInputs()
              loading.set(false)
              newNarrations.set(!newNarrations.get())
            }
          })
          .catch(() => {
            loading.set(false)
          })
      } else {
        invalidRequest.set(true)
      }
      loading.set(false)
    }
  }

  useEffect(() => {
    if (reportedAtVal) {
      const days = dayjs(reportedAtVal)
      setDateOfReport(days.isValid() ? days : null)
    }
  }, [reportedAtVal])

  const isUpdatingOccurrence = useRef(false)

  useEffect(() => {
    if (isUpdatingOccurrence.current) return

    isUpdatingOccurrence.current = true
    occurrenceId.set(Number(searchOccurrenceVal))

    setTimeout(() => {
      isUpdatingOccurrence.current = false
    }, 0)
  }, [searchOccurrenceVal])

  useEffect(() => {
    if (isUpdatingOccurrence.current || !save.value) return

    const occurrenceValue = occurrenceId.value
    const searchValue = searchOccurrenceVal

    isUpdatingOccurrence.current = true

    if (occurrenceValue && !searchValue) {
      searchOccurrence.set(String(occurrenceValue))
    } else if (!occurrenceValue && searchValue) {
      searchOccurrence.set('')
    }

    setTimeout(() => {
      isUpdatingOccurrence.current = false
    }, 0)
  }, [save.value, occurrenceId.value])

  useEffect(() => {
    if (showCheckbox.value && isAssociateMode.value && !save.value) {
      setTimeout(() => {
        setDateOfReport(null)
        notes.set('')
        searchOccurrence.set('')
        searchDispatches.set('')
        reportedAt.set(null)
        occurrenceId.set(null)
        latitudeCamera.set(null)
        longitudeCamera.set(null)
        cameraId.set(null)
        invalidRequest.set(false)
        subtypeId.set(null)
        typeId.set(null)
        verifyType.set(false)
        verifySubtype.set(true)
        createOccurrence.set(false)
      }, 0)
    }
  }, [showCheckbox.value, isAssociateMode.value])

  useEffect(() => {
    if (!showCheckbox.value || !isAssociateMode.value || save.value) return

    const timeoutId = setTimeout(() => {
      handleClearInputs()
    }, 50)

    return () => clearTimeout(timeoutId)
  }, [showCheckbox.value, isAssociateMode.value, save.value, handleClearInputs])

  useEffect(() => {
    if (cameraIdVal || searchOccurrenceVal) {
      const focusInput = () => {
        if (typeof document !== 'undefined') {
          const active = document.activeElement as HTMLElement | null
          if (active === occurrenceInputRef.current) return
        }
        inputRef.current?.focus()
      }

      requestAnimationFrame(focusInput)
    }
  }, [cameraIdVal, searchOccurrenceVal])

  useEffect(() => {
    if (createOccurrenceVal) {
      searchOccurrence.set('')
      searchDispatches.set('')
    }
  }, [createOccurrenceVal])

  useEffect(() => {
    if (!reportedAtVal && !dateOfReport) {
      const now = dayjs()
      setDateOfReport(now)
      reportedAt.set(now.format('YYYY-MM-DD HH:mm:ss'))
    }
  }, [reportedAtVal, dateOfReport])

  const SelectedReportsList = (
    <List dense sx={{ flex: 1, overflow: 'auto' }}>
      {checkedReports.map((report) => (
        <ListItem key={report.id.get()} sx={{ py: 0.5 }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" noWrap>
              <strong>{translate('Camera')}: </strong>
              {report.cameraName.get()}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {report.notes.get()}
            </Typography>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              {dayjs(report.reportedAt.get()).format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Box>
        </ListItem>
      ))}
    </List>
  )

  const hasCleared = useRef(false)

  useEffect(() => {
    if (showCheckbox.value && isAssociateMode.value && !save.value && !hasCleared.current) {
      hasCleared.current = true
      
      setDateOfReport(null)
      notes.set('')
      searchOccurrence.set('')
      searchDispatches.set('')
      reportedAt.set(null)
      occurrenceId.set(null)
      latitudeCamera.set(null)
      longitudeCamera.set(null)
      cameraId.set(null)
      invalidRequest.set(false)
      subtypeId.set(null)
      typeId.set(null)
      verifyType.set(false)
      verifySubtype.set(true)
      createOccurrence.set(false)
    } else if (!showCheckbox.value || !isAssociateMode.value) {
      hasCleared.current = false
    }
  }, [showCheckbox.value, isAssociateMode.value, save.value])

  return (
    <Grid
      container
      columns={12}
      sx={{ padding: '0px 8px', height: '100%' }}
      spacing={2}
    >
      <Grid
        item
        xs={12}
        direction="row"
        sx={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Grid
          item
          xs={5}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: showCheckbox.value ? 'auto' : '100%',
            justifyContent: save.value ? 'flex-start' : 'flex-start',
          }}
        >
          {/* Campo de Despacho - Oculto no modo de edição */}
          {!save.value && (
            <Box>
              <DispatchSearchBox
                currentDispatch={(value) => {
                  if (value) {
                    searchOccurrence.set(
                      value?.occurrence ? String(value?.occurrence.id) : ''
                    )
                  } else {
                    searchOccurrence.set('')
                  }
                }}
                dispatchValue={searchDispatches}
                isOpen={isSearchDialog}
                isValid={!createOccurrence.get() && !save.get()}
                size="small"
                isReport={true}
              />
            </Box>
          )}

          {/* Campo de Ocorrência - Oculto no modo de edição */}
          {!save.value && (
            <Box>
              <InputSearchWithClear
                fullWidth
                placeholder={translate('Occurrence')}
                search={searchOccurrence}
                inputRef={occurrenceInputRef}
                disabled={createOccurrence.value}
                onClear={() => {
                  searchDispatches.set('')
                  occurrenceId.set(null)
                }}
              />
            </Box>
          )}

          {/* Campo de Câmera - EDITÁVEL no modo de edição */}
          {!showCheckbox.value && (
            <Box>
              <FilterAutocompleteCamera
                cameraId={cameraId.value}
                onCameraChange={(camera) => {
                  cameraId.set(camera?.id ?? null)
                  latitudeCamera.set(camera?.latitude ?? null)
                  longitudeCamera.set(camera?.longitude ?? null)
                }}
                error={invalidRequest.value && validation(cameraId).invalid()}
                disabled={isAssociateMode.value}
              />
            </Box>
          )}

          {/* Campo de Data do Relato - EDITÁVEL no modo de edição */}
          {!showCheckbox.value && (
            <Box>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={'pt-br'}
              >
                <InputDateTime
                  disabled={false}
                  sx={{ width: '100%' }}
                  inputFormat="DD/MM/YYYY HH:mm"
                  placeholder={translate('dd/mm/yyyy')}
                  dateTime={dateOfReport}
                  onDateTimeChange={(date) => {
                    reportedAt.set(dayjs(date).format('YYYY-MM-DD HH:mm:ss'))
                  }}
                  label={translate('Report date')}
                  error={
                    invalidRequest.value && validation(reportedAt).invalid()
                  }
                />
              </LocalizationProvider>
            </Box>
          )}
        </Grid>

        <Grid
          item
          xs={7}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1 }} />
          {showCheckbox.value ? (
            <Paper
              variant="outlined"
              sx={{
                height: 200,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ padding: 1, borderBottom: '1px solid #e0e0e0' }}
              >
                {translate('Selected Reports for Association')}
              </Typography>
              {SelectedReportsList}
              {checkedReportsCount === 0 && (
                <Box sx={{ padding: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('No reports selected')}
                  </Typography>
                </Box>
              )}
            </Paper>
          ) : (
            <TextField
              inputRef={inputRef}
              disabled={!!showCheckbox.value}
              error={invalidRequest.get() && validation(notes).invalid()}
              multiline
              rows={8}
              sx={{ width: '100%' }}
              placeholder={translate('Write something')}
              value={notes.get() ?? ''}
              onChange={(event) => notes.set(event.target.value)}
              variant="outlined"
            />
          )}
        </Grid>
      </Grid>

      <Grid
        item
        xs={12}
        direction="row"
        sx={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          {/* Modo de Edição */}
          {save.value && (
            <>
              <Tooltip title={translate('Save')}>
                <span>
                  <IconButton
                    disabled={
                      loading.value ||
                      !cameraId.value ||
                      !reportedAt.value ||
                      !notes.value ||
                      !notes.value?.trim()
                    }
                    onClick={handleSend}
                  >
                    <SaveAsIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={translate('Cancel Edit')}>
                <span>
                  <IconButton onClick={handleCancelEdit} color="secondary">
                    <EditOff />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}

          {/* Modo de Associação */}
          {showCheckbox.value && (
            <>
              <Grid item xs={12}>
                <Tooltip title={translate('Create occurrence with reports')}>
                  <span>
                    <IconButton
                      disabled={!hasCheckedReports || !!occurrenceId.value}
                      onClick={() => {
                        openTypeModal.set(true)
                      }}
                    >
                      <NoteAddIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>

              {/* Botão de associar ao despacho */}
              <Tooltip title={translate('Associate to Dispatch')}>
                <span>
                  <IconButton
                    disabled={
                      loading.value ||
                      !hasCheckedReports ||
                      (!createOccurrence.value && !occurrenceId.value)
                    }
                    onClick={handleSend}
                  >
                    <SaveAsIcon />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Botão de cancelar associação */}
              <Tooltip title={translate('Cancel Association')}>
                <span>
                  <IconButton
                    onClick={handleCancelAssociation}
                    color="secondary"
                  >
                    <EditOff />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}

          {/* Modo Normal de Criação */}
          {!showCheckbox.value && !save.value && (
            <>
              <Grid item xs={12}>
                <Tooltip title={translate('Create occurrence with report')}>
                  <span>
                    <IconButton
                      disabled={
                        !cameraId.value ||
                        !reportedAt.value ||
                        !notes.value?.trim() ||
                        !!occurrenceId.value
                      }
                      onClick={() => {
                        openTypeModal.set(true)
                      }}
                    >
                      <NoteAddIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
              <Tooltip title={translate('Save')}>
                <span>
                  <IconButton
                    disabled={
                      loading.value ||
                      !cameraId.value ||
                      !reportedAt.value ||
                      !notes.value?.trim()
                    }
                    onClick={handleSend}
                  >
                    <SaveAsIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
        <TypeSubtypeModal
          open={openTypeModal.value}
          onClose={() => openTypeModal.set(false)}
          typeIdValue={typeId.value ?? null}
          onTypeChange={(type) => {
            typeId.set(type?.id ? Number(type.id) : null)
            if (!type) {
              subtypeId.set(null)
            }
          }}
          subtypeIdValue={subtypeId.value ?? null}
          onSubtypeChange={(subtype) =>
            subtypeId?.set(subtype?.id ? Number(subtype.id) : null)
          }
          enabledField={enabledField}
          verifyType={verifyType.value}
          verifySubtype={verifySubtype.value}
          invalidRequest={invalidRequest.value}
          hasOccurrence={!!occurrenceId.value}
          occurrenceId={occurrenceId.value}
          onSave={async () => {
            openTypeModal.set(false)
            loading.set(true)

            try {
              if (showCheckbox.get()) {
                const reportIds = reports
                  .filter((report) => report.checked.get())
                  .map((report) => report.id.get())
                const checkedReports = reports.filter((report) =>
                  report.checked.get()
                )
                const sortedReports = [...checkedReports].sort(
                  (reportA, reportB) =>
                    new Date(reportA.createdAt.get() || '').getTime() -
                    new Date(reportB.createdAt.get() || '').getTime()
                )
                const oldestReport = sortedReports[0]

                const response = await associateOccurrence({
                  occurrenceId: null,
                  surveillanceReportIds: reportIds,
                  toCreateOccurrence: true,
                  typeId: typeId.get(),
                  subtypeId: subtypeId.get(),
                  latitude: latitudeCamera.get(),
                  longitude: longitudeCamera.get(),
                  cameraId: oldestReport?.cameraId.get() || null,
                })

                if (response?.status === 200) {
                  notify({
                    message: translate('Reports associated with success'),
                    type: 'success',
                  })
                }
                handleClearInputs()
                showCheckbox.set(false)
                newNarrations.set(!newNarrations.get())
              } else {
                const response = await sendNewSurveillanceReporting({
                  occurrenceId: null,
                  cameraId: cameraId.get(),
                  notes: notes.get(),
                  reportedAt: dayjs(reportedAt.get()).utc().toJSON(),
                  toCreateOccurrence: true,
                  typeId: typeId.get(),
                  subtypeId: subtypeId.get(),
                  latitude: latitudeCamera.get(),
                  longitude: longitudeCamera.get(),
                })

                if (response?.status === 200) {
                  notify({
                    message: translate('Narration saved successfully'),
                    type: 'success',
                  })
                }
                handleClearInputs()
                newNarrations.set(!newNarrations.get())
              }
            } catch (err) {
              console.error(err)
              notify({
                message: translate('Error saving narration'),
                type: 'error',
              })
            } finally {
              loading.set(false)
            }
          }}
        />
      </Grid>
    </Grid>
  )
}
